import { Component, type OnInit, signal, inject, computed, viewChild, output, Input } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormBuilder, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BasicInfoStepComponent } from "./basic-info-step/basic-info-step.component";
import { TypePublicStepComponent } from "./type-public-step/type-public-step.component";
import { MultimediaStepComponent } from "./multimedia-step/multimedia-step.component";
import { BenefitsStepComponent } from "./benefits-step/benefits-step.component";
import { PricingStepComponent } from "./pricing-step/pricing-step.component";
import { CapacitiesStepComponent } from "./capacities-step/capacities-step.component";
import { ReviewPublishStepComponent } from "./review-publish-step/review-publish-step.component";
import { FunctionalType } from "src/app/enums/functional-type";
import { DropDownKeywords, DropdownOption } from "src/app/models/shared/dropdown-option";
import { DropdownService } from "src/app/services/dropdown.service";
import { CatalogItemVersionStatus } from "src/app/enums/catalog-item-version-status";
import { CatalogItemService } from "src/app/services/catalog-item.service";
import { AddCatalogBundle, AddCatalogItemBenefit, AddCatalogItemCapacity, AddCatalogItemMedia, AddCatalogItemPlan, GetCatalogItemBenefit, GetCatalogItemMedia, GetCatalogItemPlan, GetCatalogVersionBundle } from "src/app/models/catalogs/catalog";
import Swal from "sweetalert2";
import { finalize } from "rxjs";
import { LoadingService } from "src/app/services/loading.service";
import { FinancialParameterService } from "src/app/services/financial-parameter.service";
import { GetFinancialParameter } from "src/app/models/financial-parameter/get";

type WizardMode = "create" | "edit" | "view";

export interface WizardData {
  basicInfo: {
    id: number | null,
    title: string;
    subtitle: string;
    shortDescription: string;
    longDescription: string;
    coverFile?: string;
    coverPreview?: string;
    coverFileId?: number | null,
  };
  typePublic: {
    functionalTypeId: FunctionalType;
    functionalType?: string | null;
    serviceId?: number | null;
    service?: string | null;
    eligibleCompany: boolean;
    eligibleCandidate: boolean;
    eligibleFreelance: boolean;
  };
  multimedia: {
    coverPreview?: string;
    coverFile?: string;
    coverFileId?: number | null,
    gallery: Array<{
      mediaType: string;
      id: number | null;
      versionId: number;
      fileId?: number;
      caption: string;
      sortOrder: number;
      isEnabled: boolean;
      fileBase64: string | null;
      fileName: string | null;
      contentType: string | null;
    }>;
  };
  benefits: Array<{
    id: number | null;
    versionId: number;
    shortText: string;
    iconName: string;
    sortOrder: number;
    isEnabled: boolean
  }>;
  pricing: {
    basePrice: number | null;
    appliesITBIS: boolean;
    plans: Array<{
      id: number | null;
      periodUnitId: number;
      periodQty: number;
      name: string;
      price: number;
      isActive: boolean;
      isDefault: boolean;
      appliesITBIS: boolean;
    }>;
  };

  capacities: {
    inventoryEnabled: boolean;
    initialStock?: number;
    allowOversell: boolean;
    seatsEnabled: boolean;
    totalSeats?: number;
    holdTTLMinutes?: number;
    preApprovalRequired: boolean;
    paymentWindowMinutes?: number;
  };
}

@Component({
  selector: "app-modal-catalog-wizard",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    BasicInfoStepComponent,
    TypePublicStepComponent,
    MultimediaStepComponent,
    BenefitsStepComponent,
    PricingStepComponent,
    CapacitiesStepComponent,
    ReviewPublishStepComponent,
  ],
  templateUrl: "./modal-catalog-wizard.component.html",
  styleUrls: ["./modal-catalog-wizard.component.css"],
})
export class ModalCatalogWizardComponent implements OnInit {
  @Input() modalId = 'modalCatalogWizard';
  @Input() showTitle = true;

  private readonly dropdownService = inject(DropdownService);
  private readonly catalogItemService = inject(CatalogItemService);
  private financialParameterService = inject(FinancialParameterService);
  loadingSvc = inject(LoadingService);

  changeData = output<void>();

  step = signal(1);
  get totalSteps() { return this.mode === "view" ? 6 : 7; }
  isSavingDraft = signal(false);

  get stepNames() {
    return this.mode === "view"
      ? ["Básicos", "Tipo & Público", "Multimedia", "Beneficios", "Precio", "Capacidades"]
      : ["Básicos", "Tipo & Público", "Multimedia", "Beneficios", "Precio", "Capacidades", "Revisar & Publicar"];
  }

  wizardData = signal<WizardData>({
    basicInfo: {
      id: null,
      title: "",
      subtitle: "",
      shortDescription: "",
      longDescription: "",
      coverFile: "",
      coverFileId: null
    },
    typePublic: {
      functionalTypeId: FunctionalType.Service,
      eligibleCompany: false,
      eligibleCandidate: false,
      eligibleFreelance: false,
    },
    multimedia: {
      coverFile: "",
      gallery: [],
    },
    benefits: [],
    pricing: {
      basePrice: null,
      appliesITBIS: false,
      plans: [],
    },
    capacities: {
      inventoryEnabled: false,
      allowOversell: false,
      seatsEnabled: false,
      preApprovalRequired: false,
    },
  });

  availableServicesOptions: DropdownOption[] = [];
  functionalTypeOptions: DropdownOption[] = [];
  periodUnitOptions: DropdownOption[] = [];
  faIconsOptions: DropdownOption[] = [];
  financialParameter: GetFinancialParameter | null = null;

  confirmationChecked = signal<boolean>(false);

  progressPercentage = computed(() => Math.round((this.step() / this.totalSteps) * 100));

  public basicInfoStepComponent = viewChild(BasicInfoStepComponent);
  public typePublicStepComponent = viewChild(TypePublicStepComponent);
  public multimediaStepComponent = viewChild(MultimediaStepComponent);
  public benefitsStepComponent = viewChild(BenefitsStepComponent);
  public pricingStepComponent = viewChild(PricingStepComponent);
  public capacitiesStepComponent = viewChild(CapacitiesStepComponent);
  public reviewPublishStepComponent = viewChild(ReviewPublishStepComponent);

  catalogItemVersionStatus = CatalogItemVersionStatus;

  catalogVersionBundle: GetCatalogVersionBundle | null = null;

  public mode: WizardMode = "create";
  private editingVersionId: number | null = null;
  private catalogItemId: number | null = null;

  ngOnInit(): void {
    this.getOptions();
  }

  getOptions() {
    this.dropdownService.GetOptions(DropDownKeywords.Services).subscribe({
      next: (res) => (this.availableServicesOptions = res),
    });
    this.dropdownService.GetOptions(DropDownKeywords.PeriodUnit).subscribe({
      next: (res) => (this.periodUnitOptions = res),
    });
    this.dropdownService.GetOptions(DropDownKeywords.FaIcons).subscribe({
      next: (res) => (this.faIconsOptions = res),
    });
    this.dropdownService.GetOptions(DropDownKeywords.FunctionalType).subscribe({
      next: (res) => (this.functionalTypeOptions = res),
    });
    this.financialParameterService.getByCode('ITBIS').subscribe({
      next: res => {
        this.financialParameter = res;
      }
    })
  }

  openForEdit(versionId: number) {
    this.resetWizardData();
    this.mode = "edit";
    this.editingVersionId = versionId;
    this.loadFromBundle(versionId, true);
  }

  openForView(versionId: number) {
    this.resetWizardData();
    this.mode = "view";
    this.editingVersionId = versionId;
    this.loadFromBundle(versionId, true);
  }

  private loadFromBundle(versionId: number, openModalAfter = false) {
    this.loadingSvc.show();
    this.catalogItemService.getCatalogVersionBundle(versionId).pipe(
      finalize(() => {
        this.loadingSvc.hide();
      })
    ).subscribe({
      next: (bundle: GetCatalogVersionBundle) => {
        this.catalogVersionBundle = bundle;
        this.applyBundleToWizard(bundle);
        this.catalogItemId = bundle.item.id;
        if (openModalAfter) this.openModal();
      },
      error: (error) => {
        Swal.fire({
          title: 'Error',
          text: error[0],
          icon: 'error',
          confirmButtonColor: '#d33'
        });
      }
    });
  }

  private applyBundleToWizard(b: GetCatalogVersionBundle) {
    const basicInfo = {
      id: b.item.id,
      title: b.version.title ?? "",
      subtitle: b.version.subtitle ?? "",
      shortDescription: b.version.shortDescription ?? "",
      longDescription: b.version.longDescription ?? "",
      coverFile: "",
      coverFileId: b.version.coverFileId
    };

    const typePublic = {
      functionalTypeId: b.version.functionalTypeId,
      functionalType: b.version.functionalTypeName,
      serviceId: b.item?.availableServiceId,
      service: b.item.availableServiceName,
      eligibleCompany: !!b.version.eligibleCompany,
      eligibleCandidate: !!b.version.eligibleCandidate,
      eligibleFreelance: !!b.version.eligibleFreelance
    };

    const map: Record<string, string> = {
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      mp4: "video/mp4",
      mov: "video/quicktime",
      mp3: "audio/mpeg",
      wav: "audio/wav",
      pdf: "application/pdf"
    };

    const gallery = (b.media ?? []).map((m: GetCatalogItemMedia, idx: number) => {
      const ext = m.fileName?.split(".").pop()?.toLowerCase() ?? "";
      return {
        mediaType: m.mediaType ?? "image",
        id: m.id ?? null,
        versionId: m.versionId,
        fileId: m.fileId,
        caption: m.caption ?? "",
        sortOrder: typeof m.sortOrder === "number" ? m.sortOrder : idx + 1,
        isEnabled: !!m.isEnabled,
        fileBase64: null,
        fileName: m.fileName,
        contentType: map[ext] ?? "application/octet-stream"
      };
    });

    const multimedia = {
      coverFile: "",
      coverFileId: b.version.coverFileId,
      gallery
    };

    const benefits = (b.benefits ?? []).map((x: GetCatalogItemBenefit) => ({
      id: x.id ?? null,
      versionId: x.versionId,
      shortText: x.shortText ?? x.title ?? "",
      iconName: x.iconName ?? "",
      sortOrder: x.sortOrder ?? 0,
      isEnabled: x.isEnabled
    }));

    const pricing = {
      basePrice: b.version.basePrice ?? 0,
      appliesITBIS: !!b.version.appliesITBIS,
      plans: (b.plans ?? []).map((p: GetCatalogItemPlan, idx: number) => ({
        id: p.id,
        periodUnitId: p.periodUnitId ?? 0,
        periodUnitName: p.periodUnitName,
        periodQty: p.periodQty ?? 1,
        name: p.name ?? `Plan ${idx + 1}`,
        price: p.price ?? 0,
        isActive: !!p.isActive,
        isDefault: !!p.isDefault,
        appliesITBIS: p.appliesITBIS ?? false
      }))
    };

    const c = b.capacity;
    const capacities = c
      ? {
        inventoryEnabled: !!c.inventoryEnabled,
        initialStock: c.initialStock ?? undefined,
        allowOversell: !!c.allowOversell,
        seatsEnabled: !!c.seatsEnabled,
        totalSeats: c.totalSeats ?? undefined,
        holdTTLMinutes: c.holdTTLMinutes ?? undefined,
        preApprovalRequired: !!c.preApprovalRequired,
        paymentWindowMinutes: c.paymentWindowMinutes ?? undefined
      }
      : {
        inventoryEnabled: false,
        allowOversell: false,
        seatsEnabled: false,
        preApprovalRequired: false
      };

    this.wizardData.set({
      basicInfo,
      typePublic,
      multimedia,
      benefits,
      pricing,
      capacities
    });
    this.updateCoverFile();
    this.editingVersionId = b.version.id;
  }

  updateCoverFile() {
    this.wizardData.update((current) => ({
      ...current,
      ["basicInfo"]: {
        ...current["basicInfo"],
        coverPreview: current["multimedia"].coverPreview,
        coverFileId: current["multimedia"].coverFileId,
        coverFile: current["multimedia"].coverFile,
      },
    }));
  }

  updateWizardData(stepKey: keyof WizardData, data: any) {
    this.wizardData.update((current) => ({
      ...current,
      [stepKey]: data,
    }));
    if (stepKey === "multimedia") {
      this.updateCoverFile();
    }
  }

  getStepClass(stepIndex: number): string {
    const currentStep = this.step();
    if (stepIndex === currentStep) {
      return "text-primary fw-medium";
    } else if (stepIndex < currentStep) {
      return "text-muted";
    } else {
      return "text-muted opacity-50";
    }
  }

  get pages(): { [key: number]: boolean | undefined } {
    return {
      1: this.basicInfoStepComponent()?.validate(),
      2: this.typePublicStepComponent()?.validate(),
      3: this.multimediaStepComponent()?.validate(),
      4: this.benefitsStepComponent()?.validate(),
      5: this.pricingStepComponent()?.validate(),
      6: this.capacitiesStepComponent()?.validate(),
    };
  }

  private isCurrentStepValid(): boolean {
    const valid = this.pages[this.step()];
    return typeof valid === "boolean" ? valid : true;
  }

  next() {
    // if (!this.isCurrentStepValid()) return;
    if (this.step() < this.totalSteps) {
      this.step.update((current) => current + 1);
    }
  }

  prev() {
    if (this.step() > 1) {
      this.step.update((current) => current - 1);
    }
  }

  private mapToAddCatalogBundle(
    statusId: CatalogItemVersionStatus
  ): AddCatalogBundle {
    const data = this.wizardData();
    const media: AddCatalogItemMedia[] | null =
      (data.multimedia?.gallery ?? []).map((m, idx) => ({
        id: m.id,
        versionId: this.editingVersionId ?? 0,
        fileId: typeof (m as any).fileId === "number" ? (m as any).fileId : undefined,
        mediaType: (m as any).mediaType ?? (m as any).type ?? "image",
        caption: m.caption ?? "",
        sortOrder: typeof (m as any).sortOrder === "number" ? (m as any).sortOrder : idx + 1,
        isEnabled: !!m.isEnabled,
        fileBase64: (m as any).fileBase64 ?? null,
        fileName: (m as any).fileName ?? null,
        contentType: (m as any).contentType ?? null,
      })) || null;

    const benefits: AddCatalogItemBenefit[] | null =
      (data.benefits ?? []).map((b) => ({
        id: b.id ?? null,
        versionId: this.editingVersionId ?? 0,
        title: b.shortText,
        shortText: b.shortText ?? "",
        iconName: b.iconName ?? "",
        sortOrder: b.sortOrder ?? 0,
        isEnabled: b.isEnabled
      })) || null;

    const plans: AddCatalogItemPlan[] | null =
      (data.pricing?.plans ?? []).map((p, idx) => ({
        id: p.id,
        versionId: this.editingVersionId ?? 0,
        periodUnitId: p.periodUnitId,
        periodQty: p.periodQty ?? 1,
        price: p.price ?? 0,
        name: p.name ?? `Plan ${idx + 1}`,
        isActive: !!p.isActive,
        isDefault: !!p.isDefault,
        appliesITBIS: p.appliesITBIS ?? false,
      })) || null;

    const capacity: AddCatalogItemCapacity | null = {
      versionId: this.editingVersionId ?? 0,
      inventoryEnabled: !!data.capacities?.inventoryEnabled,
      initialStock: data.capacities?.initialStock ?? null,
      allowOversell: !!data.capacities?.allowOversell,
      seatsEnabled: !!data.capacities?.seatsEnabled,
      totalSeats: data.capacities?.totalSeats ?? null,
      holdTTLMinutes: data.capacities?.holdTTLMinutes ?? null,
      preApprovalRequired: !!data.capacities?.preApprovalRequired,
      paymentWindowMinutes: data.capacities?.paymentWindowMinutes ?? null,
    };

    const addCatalogBundle: AddCatalogBundle = {
      item: {
        id: this.catalogItemId,
        functionalTypeId: data.typePublic.functionalTypeId,
        availableServiceId: (data.typePublic.serviceId as any) ?? null,
        isDeleted: false,
      },
      version: {
        id: this.editingVersionId,
        catalogItemId: data.basicInfo.id ?? 0,
        statusId,
        title: data.basicInfo.title?.trim() ?? "",
        subtitle: data.basicInfo.subtitle?.trim() ?? "",
        shortDescription: data.basicInfo.shortDescription?.trim() ?? "",
        longDescription: data.basicInfo.longDescription?.trim() ?? "",
        coverFile: data.multimedia.coverFile ?? '',
        coverFileId: data.multimedia?.coverFileId ?? null,
        eligibleCompany: !!data.typePublic.eligibleCompany,
        eligibleCandidate: !!data.typePublic.eligibleCandidate,
        eligibleFreelance: !!data.typePublic.eligibleFreelance,
        basePrice: data.pricing?.basePrice ?? null,
        appliesITBIS: !!data.pricing?.appliesITBIS,
        changeSummary: statusId === CatalogItemVersionStatus.Draft ? "Borrador" : "Publicación",
        publishNow: statusId === CatalogItemVersionStatus.Published
      },
      media,
      benefits,
      plans,
      capacity,
    };

    return addCatalogBundle;
  }

  private showAlert(type: "publish-success" | "publish-error" | "draft-success" | "draft-error",
    text?: string
  ) {
    switch (type) {
      case "publish-success":
        Swal.fire({
          icon: "success",
          title: "¡Producto publicado exitosamente!",
          showConfirmButton: true,
          timer: 2000
        });
        this.changeData.emit();
        break;

      case "publish-error":
        Swal.fire({
          icon: "error",
          title: "Error al publicar el producto",
          showConfirmButton: true,
          text: text ?? "Inténtalo de nuevo.",
        });
        break;

      case "draft-success":
        Swal.fire({
          icon: "success",
          title: "Borrador guardado",
          showConfirmButton: true,
          timer: 1500
        });
        this.changeData.emit();
        break;

      case "draft-error":
        Swal.fire({
          icon: "error",
          title: "No se pudo guardar el borrador",
          showConfirmButton: true,
          text: text ?? "Inténtalo de nuevo.",
        });
        break;
    }
  }


  submitPublish() {
    const reviewCmp = this.reviewPublishStepComponent();
    if (reviewCmp) {
      const allErrors = reviewCmp.getAllErrors();
      if (Object.keys(allErrors).length > 0) {
        const htmlList = Object.entries(allErrors)
          .map(([section, errs]) => `
          <div style="margin-bottom:10px">
            <strong>${section}</strong>
            <ul style="margin:4px 0 0 16px; padding:0">
              ${errs.map(e => `<li>${e}</li>`).join("")}
            </ul>
          </div>
        `)
          .join("");

        Swal.fire({
          icon: "error",
          title: "Faltan datos obligatorios",
          html: `<div style="text-align:left">${htmlList}</div>`,
          confirmButtonColor: "#d33"
        });
        return;
      }
    }

    this.loadingSvc.show();
    const addCatalogBundle = this.mapToAddCatalogBundle(CatalogItemVersionStatus.Published);

    this.catalogItemService.addCatalogBundle(addCatalogBundle)
      .pipe(finalize(() => this.loadingSvc.hide()))
      .subscribe({
        next: () => {
          this.closeModal();
          this.showAlert("publish-success");
          this.changeData.emit();
        },
        error: (error) => {
          this.showAlert("publish-error", error[0]);
        },
      });
  }

  saveDraft() {
    const reviewCmp = this.reviewPublishStepComponent();
    if (reviewCmp) {
      const allErrors = reviewCmp.getAllErrors();
      if (Object.keys(allErrors).length > 0) {
        const htmlList = Object.entries(allErrors)
          .map(([section, errs]) => `
          <div style="margin-bottom:10px">
            <strong>${section}</strong>
            <ul style="margin:4px 0 0 16px; padding:0">
              ${errs.map(e => `<li>${e}</li>`).join("")}
            </ul>
          </div>
        `)
          .join("");

        Swal.fire({
          icon: "warning",
          title: "No se puede guardar el borrador",
          html: `<div style="text-align:left">${htmlList}</div>`,
          confirmButtonColor: "#d33"
        });
        return; // no se guarda el borrador si hay errores
      }
    }
    this.isSavingDraft.set(true);
    const addCatalogBundle = this.mapToAddCatalogBundle(CatalogItemVersionStatus.Draft);

    this.catalogItemService.addCatalogBundle(addCatalogBundle).subscribe({
      next: () => {
        this.closeModal();
        this.showAlert("draft-success");
        this.isSavingDraft.set(false);
        this.changeData.emit();
      },
      error: (error) => {
        console.error("Error saving draft:", error);
        this.showAlert("draft-error");
        this.isSavingDraft.set(false);
      },
    });
  }

  closeModal() {
    const modalElement = document.getElementById(this.modalId);
    if (modalElement) {
      const modal = (window as any).bootstrap?.Modal?.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }

    this.step.set(1);
    this.loadingSvc.hide();
  }

  openModal() {
    const modalElement = document.getElementById(this.modalId);
    if (modalElement) {
      const modal = new (window as any).bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  resetWizardData() {
    this.wizardData.set({
      basicInfo: {
        id: null,
        title: "",
        subtitle: "",
        shortDescription: "",
        longDescription: "",
        coverFile: "",
        coverFileId: null
      },
      typePublic: {
        functionalTypeId: FunctionalType.Service,
        eligibleCompany: false,
        eligibleCandidate: false,
        eligibleFreelance: false,
      },
      multimedia: {
        coverFile: "",
        gallery: [],
      },
      benefits: [],
      pricing: {
        basePrice: null,
        appliesITBIS: false,
        plans: [],
      },
      capacities: {
        inventoryEnabled: false,
        allowOversell: false,
        seatsEnabled: false,
        preApprovalRequired: false,
      },
    });

    this.confirmationChecked.set(false);
    this.editingVersionId = null;
    this.catalogItemId = null;
    this.step.set(1);
  }

}